// @auctogon/core/inventory — the single canonical inventory-media
// representation (BL-767). One typed record shape replaces the legacy split of
// `inventory_items.photos` (string[]), `.videos` (string[]), and the mobile
// `.media` blob. Pure + client-safe (no `server-only`): admin-web, bidder-web,
// and the admin-catalog RN app all import it, so the shape a cataloguer writes
// is exactly the shape a buyer's gallery/card reads. IsomorphicService family
// per ADR 010 — no I/O, no writes, safe inside useMemo.
import { z } from '../../zod';

import { storagePathSchema } from '../validation/storage-path';

/**
 * The canonical persisted media record. Stored as an element of the
 * `inventory_items.media` jsonb array. `sortOrder` is 0-based display order;
 * exactly one entry per item carries `isCover: true` (enforced on write by
 * {@link normalizeMediaForWrite}).
 */
export interface InventoryMediaItem {
  id: string;
  type: 'photo' | 'video';
  /** Server-accessible URL. The mobile app maps its `remoteUrl` → `url` on sync. */
  url: string;
  caption?: string;
  /**
   * A video's poster image URL (BL-2439): DTO-only, built per response from
   * {@link posterStoragePath} by the same builder as `url`. Never persisted —
   * the write path strips it like `url` (ADR 044 — Must Consolidate: one
   * persisted locator). Absent on photos and on videos with no poster.
   */
  thumbnail?: string;
  sortOrder: number;
  isCover: boolean;
  /**
   * Storage-object path of the uploaded file, carried straight off the
   * upload-complete response so deletes address the file directly instead of
   * reverse-engineering it from the public URL (BL-958, mirroring the
   * `storagePath` that `ExternalDocument` already carries). OPTIONAL: present
   * on rows written after BL-958, absent on legacy rows (which fall back to
   * URL-derivation on delete).
   */
  storagePath?: string;
  /**
   * Storage-object path of a video's poster: one JPEG frame captured in the
   * browser at upload and stored beside the video (BL-2439). Videos only — the
   * write path drops it on a photo. Absent on videos uploaded before BL-2439
   * (no backfill) and on videos whose frame could not be captured.
   */
  posterStoragePath?: string;
}

/**
 * A media URL must be an absolute http(s) URL or a root-relative path — this
 * rejects the `javascript:` / `data:` / `vbscript:` / `file:` schemes that
 * would otherwise persist and render as stored XSS in an `<a href>` / `<img src>`
 * (BL-767 security review). Root-relative paths are allowed because seed/demo
 * media is stored relative (`/images/...`) alongside absolute Supabase Storage
 * URLs. Capped to keep a single JSONB row bounded.
 */
const isSafeMediaUrl = (value: string): boolean => {
  const s = value.trim();
  return s.length > 0 && (/^https?:\/\//i.test(s) || s.startsWith('/'));
};
const safeMediaUrl = z
  .string()
  .max(2048)
  .refine(isSafeMediaUrl, { message: 'URL must be an absolute http(s) URL or a root-relative path' });

/**
 * The most media records one lot may carry — bounds the per-row JSONB size.
 * One source for the whole-array write ({@link inventoryMediaArraySchema}) and
 * the per-item add (BL-2338).
 */
export const MAX_MEDIA_PER_LOT = 200;

/**
 * The media record's input fields, BEFORE the write-side transform drops `url`.
 * Exported so a write door can derive a stricter input from the same field
 * rules (BL-2338's per-item add requires `storagePath` and ignores
 * `sortOrder` / `isCover`) instead of restating them. Persist through
 * {@link inventoryMediaItemSchema}, never this.
 */
export const inventoryMediaItemFieldsSchema = z.object({
    id: z.string().max(200),
    type: z.enum(['photo', 'video']),
    // BL-1455: OPTIONAL on input (a paths-only client sends no url) but still
    // scheme-guarded when present, then DROPPED by the transform below. The
    // validation is not vestigial: rejecting `javascript:` before discarding
    // the field keeps the guard honest, and the identical `safeMediaUrl` on
    // `thumbnail` depends on the same refinement. BL-2439: `thumbnail` is now
    // DTO-only too (built from `posterStoragePath`), so it is checked and
    // dropped the same way.
    url: safeMediaUrl.optional(),
    caption: z.string().max(1000).optional(),
    thumbnail: safeMediaUrl.optional(),
    sortOrder: z.number().int().nonnegative(),
    isCover: z.boolean(),
    // Storage-object path — since BL-1455 the ONLY locator persisted, and the
    // sole input to the per-response URL build. Must be listed here or Zod
    // strips it on write and the field never persists.
    //
    // Guarded by the SHARED `storagePathSchema` rather than a bare
    // `z.string().max(...)`: this value is now concatenated onto a bucket URL
    // that is served to the ANONYMOUS bidder site, so a scheme, a
    // protocol-relative prefix, or `..` traversal in the stored key is no
    // longer inert. Shared with every other persisted storage path so they
    // cannot drift again — the media field and the since-deleted lienholder
    // proof field already did once, in this item's own first pass.
    storagePath: storagePathSchema('Media storage path').optional(),
    // BL-2439: a video's poster frame. The same shared guard as `storagePath`,
    // since it too is concatenated onto a public bucket URL.
    posterStoragePath: storagePathSchema('Video poster storage path').optional(),
});

/**
 * The write-side strip every media write door shares (BL-1455, BL-2439): drop
 * the DTO-only `url` and `thumbnail` (both are built per response), and drop a
 * `posterStoragePath` sent on a photo (only a video has a poster). Used by
 * {@link inventoryMediaItemSchema} and the per-item add request schema, so the
 * two doors cannot disagree on what is persisted.
 *
 * @param fields a parsed media record's fields
 * @returns the fields that persist
 */
export function toPersistedMediaFields<
  T extends { type: 'photo' | 'video'; url?: string; thumbnail?: string; posterStoragePath?: string },
>(fields: T): Omit<T, 'url' | 'thumbnail'> {
  const { url: _url, thumbnail: _thumbnail, ...persisted } = fields;
  if (persisted.type === 'video') return persisted;
  const { posterStoragePath: _poster, ...photo } = persisted;
  // `posterStoragePath` is optional on every caller's T, so dropping it keeps
  // the shape; TypeScript cannot prove that through the generic `Omit`.
  return photo as Omit<T, 'url' | 'thumbnail'>;
}

/**
 * Zod schema for a single persisted media record — validated on every write
 * (admin lot/inventory PUT + mobile sync). `url`/`thumbnail` are scheme-guarded
 * (see {@link isSafeMediaUrl}); `caption`/`id` are length-capped to bound the
 * JSONB payload — restoring the guarantees the retired `photos`/`videos`
 * schemas enforced.
 */
export const inventoryMediaItemSchema = inventoryMediaItemFieldsSchema
  // BL-1455: strip the absolute URL on the way to the database. This is the
  // single write-side choke point — every server write path funnels through
  // this schema (lots PATCH, inventory POST, auction-lot PATCH, lot-create
  // prepare, mobile sync), including the catalog app, which still sends a
  // `url` back on sync and is normalized here rather than client-side.
  //
  // Deliberately NOT done in `normalizeMediaForWrite`: the admin media manager
  // calls that on local client state, where `url` must survive to render.
  .transform(toPersistedMediaFields);

/**
 * Zod schema for the full media array — capped to bound the per-row JSONB size.
 * Its OUTPUT is the persisted shape (no `url`), not the DTO shape.
 */
export const inventoryMediaArraySchema = z.array(inventoryMediaItemSchema).max(MAX_MEDIA_PER_LOT);

/**
 * Builds an absolute, server-accessible URL from a storage-object path.
 *
 * Injected rather than imported so this module stays pure and client-safe:
 * the only real implementation reads the storage gateway, which is
 * `server-only`. Server callers pass
 * `(p) => getStorageGateway().getPublicUrl(STORAGE_BUCKET.inventoryMedia, p)`
 * — synchronous and deterministic, since `inventory-media` is a public bucket.
 */
export type MediaUrlBuilder = (storagePath: string) => string;

/**
 * Resolve a stored media record's server URL from its storage path (BL-1477).
 *
 * `storagePath` is the ONLY locator. A record without one does not resolve,
 * and {@link parseInventoryMedia} drops it.
 *
 * BL-1455 made the path the only thing the write path persists; this function
 * used to fall back to a persisted absolute `url` / `remoteUrl` / `path` for
 * rows predating it. BL-1477 cleared those columns and removed the fallback,
 * because while both existed every read had two possible answers and only
 * convention decided which won — the reachable alternative ADR 044 requires a
 * consolidation to remove. An earlier version of this docstring called that
 * branch PERMANENT; BL-1477's clear is what made it false.
 *
 * The legacy mobile shape's local-only `file://` uri was never web-usable and
 * was never accepted here either; such records (failed uploads) still drop.
 */
function resolveUrl(item: Record<string, unknown>, buildUrl: MediaUrlBuilder): string | null {
  return buildPathUrl(item.storagePath, buildUrl);
}

/** Build a URL from a stored path, or `null` when the path is absent or empty. */
function buildPathUrl(path: unknown, buildUrl: MediaUrlBuilder): string | null {
  return typeof path === 'string' && path.length > 0 ? buildUrl(path) : null;
}

/**
 * Parse the raw `inventory_items.media` jsonb blob into typed, sort-ordered
 * records. Normalizes the legacy display value `'image'` → `'photo'`, fills a
 * missing `sortOrder` from array position, mints a deterministic id when
 * absent, and drops records with no server-accessible url. Faithful on
 * `isCover` — cover fallback lives in {@link deriveCoverUrl}.
 *
 * @param raw      the jsonb value (unknown shape)
 * @param seedId   a stable id (lot/item id) used to mint deterministic record ids
 * @param buildUrl REQUIRED since BL-1477 — builds the absolute url from a
 *                 record's `storagePath`, now the only locator. It was optional
 *                 while a legacy absolute url could stand in, and omitting it
 *                 then degraded silently to that stored address. Requiring it
 *                 makes the omission a compile error instead. Client call sites
 *                 do not call this at all: they hold already-hydrated
 *                 `InventoryMediaItem[]` and want {@link pickCoverUrl}.
 * @returns typed records sorted ascending by sortOrder
 */
export function parseInventoryMedia(
  raw: unknown,
  seedId: string,
  buildUrl: MediaUrlBuilder,
): InventoryMediaItem[] {
  if (!raw || !Array.isArray(raw)) return [];
  const mapped: InventoryMediaItem[] = [];
  raw.forEach((entry, i) => {
    if (!entry || typeof entry !== 'object') return;
    // eslint-disable-next-line responsecast/no-response-type-erasure -- JSONB narrow: widening a raw media entry (`unknown`, guarded object above) from the external_media JSONB array to probe its fields; not erasing a typed DTO.
    const item = entry as Record<string, unknown>;
    const url = resolveUrl(item, buildUrl);
    if (url == null) return;
    const type = item.type === 'video' ? 'video' : 'photo';
    // BL-2439: a video's thumbnail is its poster, built like `url`. A persisted
    // `thumbnail` URL is never read: the poster path is the only locator.
    const posterStoragePath =
      type === 'video' && typeof item.posterStoragePath === 'string' && item.posterStoragePath.length > 0
        ? item.posterStoragePath
        : undefined;
    mapped.push({
      id: typeof item.id === 'string' && item.id.length > 0 ? item.id : `inv-media-${seedId}-${i}`,
      type,
      url,
      caption: typeof item.caption === 'string' ? item.caption : undefined,
      thumbnail: posterStoragePath == null ? undefined : buildUrl(posterStoragePath),
      sortOrder: typeof item.sortOrder === 'number' ? item.sortOrder : i,
      isCover: item.isCover === true,
      storagePath: typeof item.storagePath === 'string' ? item.storagePath : undefined,
      ...(posterStoragePath == null ? {} : { posterStoragePath }),
    });
  });
  return mapped.sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * Pick the cover URL from an ALREADY-PARSED media array: the `isCover` entry,
 * else the first, else `null`.
 *
 * The client half of the cover-selection seam, and the reason it exists
 * (BL-1477): three admin components called {@link deriveCoverUrl} with no
 * builder on an `InventoryMediaItem[]` the server had already hydrated. That
 * worked only because the raw-row parser fell back to a persisted `url` — the
 * exact branch BL-1477 removes — so the naive removal would have returned
 * `null` for every record and blanked every thumbnail in the Items list and
 * the Add-items modal. The call sites never wanted parsing: they hold typed
 * records whose `url` is required. This is that operation, with no builder to
 * supply and no raw shape to interpret.
 *
 * Assumes the array is already sorted by `sortOrder` — {@link
 * parseInventoryMedia} guarantees it, and every DTO reaching a client came
 * through it.
 *
 * The cover is a STILL (BUG-2039): every consumer renders it in an `<img>`, so
 * a video's own file is never returned. A video cover gives its poster
 * (`thumbnail`, BL-2439). A video with no poster gives no cover, and the caller
 * shows its placeholder: the cover stays the item the operator made Main,
 * never a different photo (Michael Lawrie, 2026-10-10). This is the one cover
 * rule for every surface — the admin lot list, the clerk thumbnail (through
 * {@link deriveCoverUrl}) and the bidder lot cards.
 *
 * A cover whose source is blank gives no cover either (BUG-2044): the offline
 * lot lists keep a Main item whose thumbnail is not on the device with its
 * source blanked, so the row shows the placeholder exactly as a posterless
 * video does, never the next photo. The parser never yields a blank `url`.
 *
 * @param media already-parsed records (a DTO array, not a raw jsonb value)
 * @returns the cover image's url, or `null` when the lot has no still to show
 */
export function pickCoverUrl(media: readonly InventoryMediaItem[]): string | null {
  if (media.length === 0) return null;
  const cover = media.find((m) => m.isCover) ?? media[0];
  // Keyed on 'video', as the parser is: anything not a video is a photo.
  const still = cover.type === 'video' ? cover.thumbnail : cover.url;
  return still == null || still.length === 0 ? null : still;
}

/**
 * Derive the cover image URL from a RAW media blob: the `isCover` entry, else
 * the first by sortOrder, else `null` — a still, per {@link pickCoverUrl}'s
 * video rule (BUG-2039). The single source of truth for "which
 * image represents this lot" now that `auction_lots.image` is gone (BL-765).
 *
 * Server-side only, since BL-1477 made `buildUrl` required. A client holding a
 * hydrated array wants {@link pickCoverUrl} instead.
 *
 * @param raw      the raw `inventory_items.media` jsonb value
 * @param seedId   a stable id used by the parser to mint deterministic ids
 * @param buildUrl builds the absolute url from a record's `storagePath`
 */
export function deriveCoverUrl(
  raw: unknown,
  seedId: string,
  buildUrl: MediaUrlBuilder,
): string | null {
  return pickCoverUrl(parseInventoryMedia(raw, seedId, buildUrl));
}

/**
 * Normalize a media array for persistence: reassign `sortOrder` by position
 * (gapless, 0-based) and force exactly one `isCover` — the first entry already
 * marked, else the first entry. Guarantees the invariant every reader relies on
 * (one resolvable cover, contiguous order).
 *
 * Generic on input shape rather than fixed to `InventoryMediaItem`
 * (`sharing.md` § 3 rule 2): it only reads and rewrites `isCover`/`sortOrder`,
 * so it serves BOTH callers — the admin media manager passing DTO records
 * (`url` present, client-side local state) and the server write paths passing
 * the post-strip persisted shape (`url` gone, BL-1455). Pinning it to the DTO
 * type would force the write paths to launder a value they just removed.
 *
 * @param items the media records to persist
 * @returns a new array safe to write to `inventory_items.media`
 * @sideEffect none — pure; returns a new array
 */
export function normalizeMediaForWrite<T extends { isCover: boolean; sortOrder: number }>(
  items: T[],
): T[] {
  if (items.length === 0) return [];
  const firstCoverIdx = items.findIndex((m) => m.isCover);
  const coverIdx = firstCoverIdx === -1 ? 0 : firstCoverIdx;
  return items.map((m, i) => ({ ...m, sortOrder: i, isCover: i === coverIdx }));
}

/**
 * BL-1979: Main is the first item. Reorders nothing; clears every cover flag
 * so {@link normalizeMediaForWrite} marks index 0 as the one cover and
 * renumbers `sortOrder`. Used after a drag (the item dragged to the front
 * becomes Main) and after "Set as main" (which moves the item to the front),
 * so the admin block, the admin grid and the bidder gallery — ordered by
 * `sortOrder` — agree on one rule.
 *
 * @param items the media records in their new order
 * @returns a new array whose first item is the only cover
 * @sideEffect none — pure; returns a new array
 */
export function withFirstAsMain<T extends { isCover: boolean; sortOrder: number }>(items: T[]): T[] {
  return normalizeMediaForWrite(items.map((m) => ({ ...m, isCover: false })));
}

/**
 * BL-1979: what makes two gallery lists "the same" for an unsaved-changes
 * check — the items, their order and which one is Main. Captions are not
 * editable, so they do not count.
 *
 * @param items a gallery list
 * @returns a comparable signature
 */
export function mediaSignature(items: readonly { id: string; isCover: boolean }[]): string {
  return items.map((m) => `${m.id}:${m.isCover ? 1 : 0}`).join('|');
}

// --- Drift guard (BL-939, relaxed by BL-1455, tightened by BL-1477) --------
// The DB-layer `MediaItem` — stamped onto `auction_lots.media` via
// `.$type<MediaItem[]>()` (BL-939 lot/inventory inversion) — MUST stay
// field-identical to the canonical `InventoryMediaItem` above, with EXACTLY
// TWO deliberate exceptions: `url`, and (BL-2439) `thumbnail`, the poster's
// URL, which is built from `posterStoragePath` the same way. `packages/db` cannot import
// `@auctogon/core`, so the two-way equality is asserted here (core already
// depends on db; `import type` is erased, no runtime edge).
//
// Why `url` diverges: the two shapes describe different things. `MediaItem` is
// what is PERSISTED, and since BL-1477 it carries NO url at all — `storagePath`
// is the only locator on disk. `InventoryMediaItem` is the DTO / in-memory
// shape, where `url` is REQUIRED because it is built per response and every
// client (including the offline-caching catalog app) depends on it being there.
//
// BL-1455 made the persisted `url` optional; BL-1477 removed it, so the guard
// tightens from "optional there, required here" to "absent there, required
// here". Equality still holds across all six other fields, so adding,
// removing, or retyping any of them on one shape without the other fails to
// compile — and the assertions below stop either side drifting on `url`:
// reintroducing a persisted `url`, or flipping the DTO's optional, is an error.
import type { MediaItem as DbMediaItem } from '@auctogon/db/types/jsonb';

type TypeEqual<A, B> = (<T>() => T extends A ? 1 : 2) extends <T>() => T extends B ? 1 : 2
  ? true
  : false;
type AssertTrue<T extends true> = T;

/* eslint-disable @typescript-eslint/no-unused-vars -- compile-time drift guards */
// Every field EXCEPT the DTO-only `url` and `thumbnail` stays field-identical
// across the two shapes.
type _MediaShapesAligned = AssertTrue<TypeEqual<Omit<InventoryMediaItem, 'url' | 'thumbnail'>, DbMediaItem>>;
// The DTO always carries a `url` — it is built per response, never absent.
type _DtoUrlIsRequired = AssertTrue<TypeEqual<InventoryMediaItem['url'], string>>;
// The persisted row carries NONE (BL-1477). Reintroducing one re-creates the
// two-answers-per-read ambiguity the item removed, so it is a compile error.
type _PersistedCarriesNoUrl = AssertTrue<TypeEqual<'url' extends keyof DbMediaItem ? true : false, false>>;
// BL-2439: the poster's URL is built per response too; only its path persists.
type _PersistedCarriesNoThumbnail = AssertTrue<TypeEqual<'thumbnail' extends keyof DbMediaItem ? true : false, false>>;
/* eslint-enable @typescript-eslint/no-unused-vars */
